import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Paper, 
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Components
import TripDetailsForm from '../components/booking/TripDetailsForm';
import VehicleSelectionForm from '../components/booking/VehicleSelectionForm';
import PassengerDetailsForm from '../components/booking/PassengerDetailsForm';
import PaymentForm from '../components/booking/PaymentForm';
import BookingSummary from '../components/booking/BookingSummary';
import BookingConfirmation from '../components/booking/BookingConfirmation';

// Common responsive components
import ResponsiveBox from '../components/common/ResponsiveBox';
import ResponsiveTypography from '../components/common/ResponsiveTypography';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

// Custom styles
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0 },
};

export interface BookingFormValues {
  // Trip Details
  tripType: 'one-way' | 'round-trip' | 'hourly';
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: Date | null;
  pickupTime: string;
  returnDate: Date | null;
  returnTime: string;
  hours: number;
  passengers: number;
  luggage: number;
  
  // Vehicle Selection
  vehicleId: string;
  
  // Passenger Details
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specialRequests: string;
  companyName?: string;
  corporateAccount?: boolean;
  
  // Payment
  cardholderName: string;
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  billingZip: string;
  paymentMethod: 'card' | 'corporate';
  cardName?: string;
  cardExpiry?: string;
  cardCvv?: string;
  saveCard?: boolean;
  acceptTerms: boolean;
}

const initialValues: BookingFormValues = {
  tripType: 'one-way',
  pickupLocation: '',
  dropoffLocation: '',
  pickupDate: null,
  pickupTime: '',
  returnDate: null,
  returnTime: '',
  hours: 4,
  passengers: 2,
  luggage: 2,
  
  vehicleId: '',
  
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  specialRequests: '',
  
  cardholderName: '',
  cardNumber: '',
  expiryDate: '',
  cvv: '',
  billingZip: '',
  paymentMethod: 'card',
  acceptTerms: false,
};

const validationSchema = Yup.object({
  // Trip Details validation
  tripType: Yup.string().required('Trip type is required'),
  pickupLocation: Yup.string().required('Pickup location is required'),
  dropoffLocation: Yup.string().when('tripType', {
    is: (val: string) => val !== 'hourly',
    then: () => Yup.string().required('Drop-off location is required'),
    otherwise: () => Yup.string(),
  }),
  pickupDate: Yup.date().nullable().required('Pickup date is required'),
  pickupTime: Yup.string().required('Pickup time is required'),
  returnDate: Yup.date().nullable().when('tripType', {
    is: 'round-trip',
    then: () => Yup.date().nullable().required('Return date is required'),
    otherwise: () => Yup.date().nullable(),
  }),
  returnTime: Yup.string().when('tripType', {
    is: 'round-trip',
    then: () => Yup.string().required('Return time is required'),
    otherwise: () => Yup.string(),
  }),
  hours: Yup.number().when('tripType', {
    is: 'hourly',
    then: () => Yup.number().min(2, 'Minimum 2 hours').max(12, 'Maximum 12 hours').required('Number of hours is required'),
    otherwise: () => Yup.number(),
  }),
  passengers: Yup.number().min(1, 'Minimum 1 passenger').max(20, 'Maximum 20 passengers').required('Number of passengers is required'),
  luggage: Yup.number().min(0, 'Minimum 0 items').max(20, 'Maximum 20 items').required('Number of luggage items is required'),
  
  // Vehicle selection validation
  vehicleId: Yup.string().required('Please select a vehicle'),
  
  // Passenger details validation
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phone: Yup.string().required('Phone number is required'),
  
  // Payment validation
  cardholderName: Yup.string().when('paymentMethod', {
    is: 'card',
    then: () => Yup.string().required('Cardholder name is required'),
    otherwise: () => Yup.string()
  }),
  cardNumber: Yup.string().when('paymentMethod', {
    is: 'card',
    then: () => Yup.string().required('Card number is required').matches(/^\d{16}$/, 'Must be 16 digits'),
    otherwise: () => Yup.string()
  }),
  expiryDate: Yup.string().when('paymentMethod', {
    is: 'card',
    then: () => Yup.string().required('Expiry date is required').matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Must be MM/YY format'),
    otherwise: () => Yup.string()
  }),
  cvv: Yup.string().when('paymentMethod', {
    is: 'card',
    then: () => Yup.string().required('CVV is required').matches(/^\d{3,4}$/, 'Must be 3 or 4 digits'),
    otherwise: () => Yup.string()
  }),
  billingZip: Yup.string().when('paymentMethod', {
    is: 'card',
    then: () => Yup.string().required('Billing ZIP is required'),
    otherwise: () => Yup.string()
  }),
  paymentMethod: Yup.string().oneOf(['card', 'corporate']).required('Payment method is required'),
  acceptTerms: Yup.bool().oneOf([true], 'You must accept the terms and conditions'),
});

const steps = ['Trip Details', 'Vehicle Selection', 'Passenger Info', 'Payment', 'Review & Confirm'];

const BookingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const seoData = pageSEO['/booking'];
  const structuredData = generateStructuredData('/booking');
  
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const nextStep = prevActiveStep + 1;
      setTimeout(scrollToTop, 0);
      return nextStep;
    });
  };
  
  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const prevStep = prevActiveStep - 1;
      setTimeout(scrollToTop, 0);
      return prevStep;
    });
  };
  
  const handleSubmit = async (values: BookingFormValues, { setSubmitting }: FormikHelpers<BookingFormValues>) => {
    if (!isConfirming) {
      setIsConfirming(true);
      return;
    }
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log('Form Values:', values);
    setSubmitting(false);
    setSubmitted(true);
  };
  
  // Determine the content to display based on the active step
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <TripDetailsForm />;
      case 1:
        return <VehicleSelectionForm />;
      case 2:
        return <PassengerDetailsForm />;
      case 3:
        return <PaymentForm />;
      case 4:
        return <BookingSummary />;
      default:
        return 'Unknown step';
    }
  };
  
  return (
    <>
      <SEO 
        pathname="/booking" 
        {...seoData} 
        structuredData={structuredData}
      />
      <motion.div
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageVariants}
        transition={{ duration: 0.3 }}
      >
        <ResponsiveBox 
        component="section" 
        className="section-spacing"
        sx={{ 
          minHeight: '100vh',
          py: { xs: 4, sm: 6, md: 8 }, 
          background: `
            linear-gradient(135deg, 
              ${alpha(theme.palette.primary.main, 0.05)} 0%, 
              ${alpha(theme.palette.secondary.main, 0.05)} 100%
            ),
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.light, 0.1)} 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 0% 100%,
              ${alpha(theme.palette.secondary.light, 0.1)} 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 100% 0%,
              ${alpha(theme.palette.primary.light, 0.1)} 0%,
              transparent 50%
            )
          `,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(
                circle at 50% 50%,
                ${alpha(theme.palette.primary.main, 0.03)} 0%,
                transparent 70%
              )
            `,
            animation: 'pulse 8s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                opacity: 1,
              },
              '50%': {
                transform: 'scale(1.2)',
                opacity: 0.8,
              },
              '100%': {
                transform: 'scale(1)',
                opacity: 1,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(
                45deg,
                transparent 48%,
                ${alpha(theme.palette.primary.main, 0.03)} 48%,
                ${alpha(theme.palette.primary.main, 0.03)} 52%,
                transparent 52%
              ),
              linear-gradient(
                -45deg,
                transparent 48%,
                ${alpha(theme.palette.primary.main, 0.03)} 48%,
                ${alpha(theme.palette.primary.main, 0.03)} 52%,
                transparent 52%
            )`,
            backgroundSize: '60px 60px',
            opacity: 0.5,
            animation: 'move 20s linear infinite',
            '@keyframes move': {
              '0%': {
                backgroundPosition: '0 0',
              },
              '100%': {
                backgroundPosition: '60px 60px',
              },
            },
            zIndex: 0,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <ResponsiveTypography 
            variant="h3" 
            component="h1" 
            textAlign="center" 
            fontWeight={600} 
            mb={{ xs: 3, sm: 4, md: 5 }}
            className="text-center-xs"
          >
            Book Your Transportation
          </ResponsiveTypography>
          
          {!submitted ? (
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: 3,
                overflow: 'hidden',
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
              }}
            >
              {/* Mobile stepper displays current step only */}
              {isMobile ? (
                <Typography 
                  variant="h5" 
                  component="h2" 
                  textAlign="center" 
                  sx={{ mb: 3, fontWeight: 500 }}
                >
                  Step {activeStep + 1}: {steps[activeStep]}
                </Typography>
              ) : (
                /* Desktop/tablet stepper shows all steps */
                <Stepper 
                  activeStep={activeStep} 
                  alternativeLabel={!isTablet}
                  orientation={isTablet ? "vertical" : "horizontal"}
                  sx={{ 
                    mb: 4,
                    '& .MuiStepLabel-label': {
                      mt: isTablet ? 0 : 1,
                      fontSize: isTablet ? '0.9rem' : 'inherit'
                    }
                  }}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
              
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({ isSubmitting, validateForm, errors, touched, setFieldTouched, values }) => (
                  <Form>
                    <Box sx={{ mb: 4 }}>
                      {getStepContent(activeStep)}
                    </Box>
                    
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        mt: 4, 
                        pt: 2,
                        borderTop: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ 
                          mr: isMobile ? 0 : 1,
                          mb: isMobile ? 2 : 0,
                          minHeight: { xs: '50px', sm: 'auto' }
                        }}
                        fullWidth={isMobile}
                      >
                        Back
                      </Button>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexDirection: isMobile ? 'column' : 'row'
                        }}
                      >
                        {activeStep === steps.length - 1 ? (
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            sx={{ 
                              minWidth: { xs: '100%', sm: '150px' },
                              minHeight: { xs: '50px', sm: 'auto' }
                            }}
                          >
                            {isSubmitting ? 'Submitting...' : isConfirming ? 'Confirm Booking' : 'Review & Submit'}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                              // Get the fields to validate based on current step
                              let fieldsToValidate: string[] = [];
                              switch (activeStep) {
                                case 0: // Trip Details
                                  fieldsToValidate = [
                                    'tripType',
                                    'pickupLocation',
                                    'dropoffLocation',
                                    'pickupDate',
                                    'pickupTime',
                                    'returnDate',
                                    'returnTime',
                                    'hours',
                                    'passengers',
                                    'luggage'
                                  ];
                                  break;
                                case 1: // Vehicle Selection
                                  fieldsToValidate = ['vehicleId'];
                                  break;
                                case 2: // Passenger Info
                                  fieldsToValidate = ['firstName', 'lastName', 'email', 'phone'];
                                  break;
                                case 3: // Payment
                                  fieldsToValidate = [
                                    'paymentMethod',
                                    'acceptTerms'
                                  ];
                                  // Add credit card fields only if payment method is card
                                  if (values.paymentMethod === 'card') {
                                    fieldsToValidate.push(
                                      'cardholderName',
                                      'cardNumber',
                                      'expiryDate',
                                      'cvv',
                                      'billingZip'
                                    );
                                  }
                                  break;
                              }

                              // Mark all fields as touched to show errors
                              fieldsToValidate.forEach(field => {
                                setFieldTouched(field, true, true);
                              });

                              // Validate the form
                              const validationErrors = await validateForm();
                              
                              // Check if there are any errors for the current step fields
                              const hasErrors = fieldsToValidate.some(field => 
                                validationErrors[field as keyof BookingFormValues] !== undefined
                              );

                              if (!hasErrors) {
                                handleNext();
                              }
                            }}
                            sx={{ 
                              minWidth: { xs: '100%', sm: '150px' },
                              minHeight: { xs: '50px', sm: 'auto' }
                            }}
                          >
                            Next
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Paper>
          ) : (
            <BookingConfirmation bookingReference={`ECT${Math.floor(100000 + Math.random() * 900000)}`} />
          )}
        </Container>
      </ResponsiveBox>
    </motion.div>
    </>
  );
};

export default BookingPage; 